import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Calendar } from './ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { X, Calendar as CalendarIcon, Clock, FileText, Banknote, CheckCircle, Loader2, Copy, Check, Download } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Logo and Signature URLs
const LOGO_URL = "https://customer-assets.emergentagent.com/job_elvis-one-more-time/artifacts/xbikd373_Logo.png";
const ARTIST_SIGNATURE_URL = "https://customer-assets.emergentagent.com/job_49ea4133-d43d-4d62-a0e3-b1ab9a95136b/artifacts/clci5l8w_Gary%20Jay%20Signature.png";

// Bank Details
const BANK_DETAILS = {
  accountName: "Gary Jay",
  sortCode: "04-00-03",
  accountNumber: "52616443"
};

// Business Details
const BUSINESS_DETAILS = {
  name: "Elvis - One More Time",
  address: "47 Flaxen Walk, Warboys, PE28 2TR",
  phone: "07391 120 152",
  email: "info@ukelvistribute.com"
};

const BookingModal = ({ isOpen, onClose }) => {
  const { toast } = useToast();
  const contractRef = useRef(null);
  const [step, setStep] = useState(0);
  const [selectedDate, setSelectedDate] = useState(null);
  const [countdown, setCountdown] = useState(null);
  const [blockedDates, setBlockedDates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [bookingId, setBookingId] = useState(null);
  const [paymentReference, setPaymentReference] = useState('');
  const [signatureName, setSignatureName] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [copiedField, setCopiedField] = useState(null);
  const [bookingData, setBookingData] = useState({
    date: null,
    venueName: '',
    venueAddress: '',
    eventType: '',
    contactName: '',
    phone: '',
    email: '',
    arrivalTime: '',
    setupTime: '',
    performanceTime: '',
    showFormat: '',
    fee: '',
    notes: ''
  });

  // Fetch blocked dates when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchBlockedDates();
    }
  }, [isOpen]);

  const fetchBlockedDates = async () => {
    try {
      const response = await fetch(`${API_URL}/api/bookings/blocked-dates`);
      const data = await response.json();
      // Convert ISO date strings to Date objects
      const dates = data.blocked_dates.map(dateStr => new Date(dateStr));
      setBlockedDates(dates);
    } catch (error) {
      console.error('Error fetching blocked dates:', error);
      // Use empty array if fetch fails
      setBlockedDates([]);
    }
  };

  if (!isOpen) return null;

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setBookingData({ ...bookingData, date });
  };

  // Generate payment reference in format EOMT + date (e.g., EOMT22/1/26)
  const generatePaymentReference = (date) => {
    const d = new Date(date);
    const day = d.getDate();
    const month = d.getMonth() + 1;
    const year = d.getFullYear().toString().slice(-2);
    return `EOMT${day}/${month}/${year}`;
  };

  const handleDateConfirm = () => {
    const ref = generatePaymentReference(selectedDate);
    setPaymentReference(ref);
    setCountdown(30);
    setStep(1);
    toast({
      title: "Date Reserved!",
      description: "You have 30 minutes to complete your booking.",
    });
  };

  const copyToClipboard = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
      toast({
        title: "Copied!",
        description: `${field} copied to clipboard`,
      });
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const downloadContractPdf = async () => {
    if (!contractRef.current) return;
    
    setDownloadingPdf(true);
    try {
      // Create a clone of the contract for PDF generation
      const contractElement = contractRef.current;
      
      // Use html2canvas to capture the contract
      const canvas = await html2canvas(contractElement, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: false,
      });
      
      const imgData = canvas.toDataURL('image/png');
      
      // Calculate dimensions for A4 paper
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
      const imgX = (pdfWidth - imgWidth * ratio) / 2;
      
      // Calculate how many pages we need
      const scaledHeight = imgHeight * ratio;
      const pageHeight = pdfHeight - 20; // margins
      const totalPages = Math.ceil(scaledHeight / pageHeight);
      
      if (totalPages === 1) {
        pdf.addImage(imgData, 'PNG', imgX, 10, imgWidth * ratio, imgHeight * ratio);
      } else {
        // Multi-page PDF
        for (let i = 0; i < totalPages; i++) {
          if (i > 0) pdf.addPage();
          
          const sourceY = (i * pageHeight / ratio);
          const sourceHeight = Math.min(pageHeight / ratio, imgHeight - sourceY);
          
          // Create a temporary canvas for this page section
          const pageCanvas = document.createElement('canvas');
          pageCanvas.width = imgWidth;
          pageCanvas.height = sourceHeight;
          const ctx = pageCanvas.getContext('2d');
          ctx.drawImage(canvas, 0, sourceY, imgWidth, sourceHeight, 0, 0, imgWidth, sourceHeight);
          
          const pageImgData = pageCanvas.toDataURL('image/png');
          pdf.addImage(pageImgData, 'PNG', imgX, 10, imgWidth * ratio, sourceHeight * ratio);
        }
      }
      
      // Generate filename with booking reference
      const dateStr = selectedDate ? selectedDate.toISOString().split('T')[0] : 'contract';
      const filename = `EOMT_Contract_${bookingData.contactName.replace(/\s+/g, '_')}_${dateStr}.pdf`;
      
      pdf.save(filename);
      
      toast({
        title: "Contract Downloaded!",
        description: "Your contract has been saved as a PDF.",
      });
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast({
        title: "Download Failed",
        description: "There was an error generating the PDF. Please try again.",
        variant: "destructive",
      });
    } finally {
      setDownloadingPdf(false);
    }
  };

  const isDateBlocked = (date) => {
    return blockedDates.some(
      (blockedDate) =>
        blockedDate.toDateString() === date.toDateString()
    );
  };

  const calculateDeposit = () => {
    const fee = parseFloat(bookingData.fee) || 0;
    return (fee * 0.2).toFixed(2);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Create booking in backend
      const response = await fetch(`${API_URL}/api/bookings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          date: selectedDate.toISOString().split('T')[0],
          venue_name: bookingData.venueName,
          venue_address: bookingData.venueAddress,
          event_type: bookingData.eventType,
          contact_name: bookingData.contactName,
          phone: bookingData.phone,
          email: bookingData.email,
          arrival_time: bookingData.arrivalTime,
          setup_time: bookingData.setupTime,
          performance_time: bookingData.performanceTime,
          show_format: bookingData.showFormat,
          fee: parseFloat(bookingData.fee),
          notes: bookingData.notes
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create booking');
      }

      const booking = await response.json();
      setBookingId(booking.id);
      setStep(2);
      
      toast({
        title: "Booking Created!",
        description: "Please review the payment details.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleContractSign = async () => {
    if (!signatureName.trim()) {
      toast({
        title: "Signature Required",
        description: "Please type your full name to sign the contract.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/bookings/${bookingId}/sign-contract`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          booking_id: bookingId,
          signature_name: signatureName
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Contract signing failed');
      }

      setStep(4);
      toast({
        title: "Contract Signed!",
        description: "Your booking is now confirmed.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFinish = () => {
    toast({
      title: "Booking Complete!",
      description: "Check your email for confirmation and contract details.",
    });
    onClose();
    // Reset state
    setStep(0);
    setSelectedDate(null);
    setBookingId(null);
    setSignatureName('');
    setPaymentReference('');
    setAgreedToTerms(false);
    setCopiedField(null);
    setDownloadingPdf(false);
    setBookingData({
      date: null,
      venueName: '',
      venueAddress: '',
      eventType: '',
      contactName: '',
      phone: '',
      email: '',
      arrivalTime: '',
      setupTime: '',
      performanceTime: '',
      showFormat: '',
      fee: '',
      notes: ''
    });
  };

  return (
    <div className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-gradient-to-br from-black to-black border-2 border-gold rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-y-auto my-8">
        {/* Header */}
        <div className="sticky top-0 bg-black/95 backdrop-blur-sm border-b border-gold/30 p-6 flex items-center justify-between z-10">
          <div>
            <h2 className="heading-display text-3xl text-gold">Book Your Event</h2>
            {countdown && (
              <p className="text-silver text-sm mt-1">
                Time remaining: {countdown} minutes
              </p>
            )}
          </div>
          <button onClick={onClose} className="text-gold hover:text-gold/80 transition-colors">
            <X size={28} />
          </button>
        </div>

        {/* Progress Indicator */}
        <div className="px-6 py-4 bg-black/50">
          <div className="flex items-center justify-between">
            {['Date', 'Details', 'Deposit', 'Contract', 'Done'].map((label, index) => (
              <div key={index} className="flex items-center">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all duration-300 ${
                    index <= step
                      ? 'bg-gold border-gold text-black'
                      : 'bg-transparent border-gold/30 text-gold/30'
                  }`}
                >
                  {index + 1}
                </div>
                {index < 4 && (
                  <div
                    className={`h-0.5 w-8 lg:w-16 mx-2 transition-all duration-300 ${
                      index < step ? 'bg-gold' : 'bg-gold/30'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 lg:p-8">
          {/* Step 0: Date Selection */}
          {step === 0 && (
            <div className="space-y-6">
              <div>
                <h3 className="heading-display text-2xl text-gold mb-4">Select Your Event Date</h3>
                <p className="text-white/80 mb-6">
                  Choose an available date for your event. Blocked dates are already booked.
                </p>
              </div>
              
              <div className="flex justify-center">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={handleDateSelect}
                  disabled={(date) => date < new Date() || isDateBlocked(date)}
                  className="rounded-lg border-2 border-gold/30 bg-black/50 p-4"
                />
              </div>
              
              {selectedDate && (
                <div className="text-center">
                  <p className="text-gold mb-4">
                    Selected: {selectedDate.toLocaleDateString('en-GB', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                  </p>
                  <Button
                    onClick={handleDateConfirm}
                    className="bg-gold text-black hover:bg-gold/90 font-semibold px-8 py-6"
                  >
                    Confirm Date & Continue
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Step 1: Booking Form */}
          {step === 1 && (
            <form onSubmit={handleFormSubmit} className="space-y-6">
              <h3 className="heading-display text-2xl text-gold mb-4">Event Details</h3>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-gold mb-2 font-semibold">Venue Name *</label>
                  <Input
                    required
                    value={bookingData.venueName}
                    onChange={(e) => setBookingData({ ...bookingData, venueName: e.target.value })}
                    className="bg-black/50 border-gold/30 focus:border-gold text-white"
                  />
                </div>
                
                <div>
                  <label className="block text-gold mb-2 font-semibold">Event Type *</label>
                  <Select
                    value={bookingData.eventType}
                    onValueChange={(value) => setBookingData({ ...bookingData, eventType: value })}
                  >
                    <SelectTrigger className="bg-black/50 border-gold/30 focus:border-gold text-white">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent className="bg-black border-gold/30">
                      <SelectItem value="wedding">Wedding</SelectItem>
                      <SelectItem value="corporate">Corporate Event</SelectItem>
                      <SelectItem value="private">Private Party</SelectItem>
                      <SelectItem value="theatre">Theatre Show</SelectItem>
                      <SelectItem value="festival">Festival</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div>
                <label className="block text-gold mb-2 font-semibold">Venue Address (including postcode) *</label>
                <Textarea
                  required
                  value={bookingData.venueAddress}
                  onChange={(e) => setBookingData({ ...bookingData, venueAddress: e.target.value })}
                  rows={3}
                  className="bg-black/50 border-gold/30 focus:border-gold text-white resize-none"
                />
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-gold mb-2 font-semibold">Contact Name *</label>
                  <Input
                    required
                    value={bookingData.contactName}
                    onChange={(e) => setBookingData({ ...bookingData, contactName: e.target.value })}
                    className="bg-black/50 border-gold/30 focus:border-gold text-white"
                  />
                </div>
                
                <div>
                  <label className="block text-gold mb-2 font-semibold">Phone *</label>
                  <Input
                    required
                    type="tel"
                    value={bookingData.phone}
                    onChange={(e) => setBookingData({ ...bookingData, phone: e.target.value })}
                    className="bg-black/50 border-gold/30 focus:border-gold text-white"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-gold mb-2 font-semibold">Email *</label>
                <Input
                  required
                  type="email"
                  value={bookingData.email}
                  onChange={(e) => setBookingData({ ...bookingData, email: e.target.value })}
                  className="bg-black/50 border-gold/30 focus:border-gold text-white"
                />
              </div>
              
              <div className="grid md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-gold mb-2 font-semibold">Arrival Time *</label>
                  <Input
                    required
                    type="time"
                    value={bookingData.arrivalTime}
                    onChange={(e) => setBookingData({ ...bookingData, arrivalTime: e.target.value })}
                    className="bg-black/50 border-gold/30 focus:border-gold text-white"
                  />
                </div>
                
                <div>
                  <label className="block text-gold mb-2 font-semibold">Setup Time *</label>
                  <Input
                    required
                    type="time"
                    value={bookingData.setupTime}
                    onChange={(e) => setBookingData({ ...bookingData, setupTime: e.target.value })}
                    className="bg-black/50 border-gold/30 focus:border-gold text-white"
                  />
                </div>
                
                <div>
                  <label className="block text-gold mb-2 font-semibold">Performance Time *</label>
                  <Input
                    required
                    type="time"
                    value={bookingData.performanceTime}
                    onChange={(e) => setBookingData({ ...bookingData, performanceTime: e.target.value })}
                    className="bg-black/50 border-gold/30 focus:border-gold text-white"
                  />
                </div>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-gold mb-2 font-semibold">Show Format *</label>
                  <Select
                    value={bookingData.showFormat}
                    onValueChange={(value) => setBookingData({ ...bookingData, showFormat: value })}
                  >
                    <SelectTrigger className="bg-black/50 border-gold/30 focus:border-gold text-white">
                      <SelectValue placeholder="Select format" />
                    </SelectTrigger>
                    <SelectContent className="bg-black border-gold/30">
                      <SelectItem value="1x60">1 x 60 minutes</SelectItem>
                      <SelectItem value="2x45">2 x 45 minutes</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <label className="block text-gold mb-2 font-semibold">Fee (£) *</label>
                  <Input
                    required
                    type="number"
                    min="0"
                    step="0.01"
                    value={bookingData.fee}
                    onChange={(e) => setBookingData({ ...bookingData, fee: e.target.value })}
                    placeholder="0.00"
                    className="bg-black/50 border-gold/30 focus:border-gold text-white"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-gold mb-2 font-semibold">Additional Notes</label>
                <Textarea
                  value={bookingData.notes}
                  onChange={(e) => setBookingData({ ...bookingData, notes: e.target.value })}
                  rows={4}
                  placeholder="Any special requirements or requests..."
                  className="bg-black/50 border-gold/30 focus:border-gold text-white resize-none"
                />
              </div>
              
              <div className="flex justify-end gap-4">
                <Button
                  type="button"
                  onClick={() => setStep(0)}
                  variant="outline"
                  className="border-gold/30 text-gold hover:bg-gold/10"
                >
                  Back
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  className="bg-gold text-black hover:bg-gold/90 font-semibold px-8"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Creating Booking...
                    </>
                  ) : (
                    'Continue to Payment'
                  )}
                </Button>
              </div>
            </form>
          )}

          {/* Step 2: Bank Transfer Details */}
          {step === 2 && (
            <div className="space-y-6">
              <h3 className="heading-display text-2xl text-gold mb-4">Secure Your Booking</h3>
              
              <div className="bg-black/50 p-6 rounded-xl border border-gold/30">
                <h4 className="text-xl text-gold mb-4 font-semibold">Fee Breakdown</h4>
                <div className="space-y-2 text-white/90">
                  <div className="flex justify-between">
                    <span>Total Fee:</span>
                    <span className="font-semibold">£{parseFloat(bookingData.fee).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-gold">
                    <span>Deposit (20%):</span>
                    <span className="font-bold text-xl">£{calculateDeposit()}</span>
                  </div>
                  <div className="flex justify-between text-sm text-silver pt-2 border-t border-gold/20">
                    <span>Balance Due (2 days before event):</span>
                    <span>£{(parseFloat(bookingData.fee) * 0.8).toFixed(2)}</span>
                  </div>
                </div>
              </div>
              
              <div className="bg-black/50 p-6 rounded-xl border border-gold/30">
                <h4 className="text-lg text-gold mb-4 font-semibold flex items-center gap-2">
                  <Banknote size={20} />
                  Bank Transfer Details
                </h4>
                <p className="text-silver text-sm mb-4">
                  Please transfer your deposit to secure your booking. Your booking will be confirmed once payment is received.
                </p>
                
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-black/30 rounded-lg border border-gold/20">
                    <div>
                      <p className="text-gold/70 text-xs uppercase">Account Name</p>
                      <p className="text-white font-semibold">{BANK_DETAILS.accountName}</p>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => copyToClipboard(BANK_DETAILS.accountName, 'Account Name')}
                      className="text-gold hover:text-gold/80"
                    >
                      {copiedField === 'Account Name' ? <Check size={16} /> : <Copy size={16} />}
                    </Button>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-black/30 rounded-lg border border-gold/20">
                    <div>
                      <p className="text-gold/70 text-xs uppercase">Sort Code</p>
                      <p className="text-white font-semibold font-mono">{BANK_DETAILS.sortCode}</p>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => copyToClipboard(BANK_DETAILS.sortCode, 'Sort Code')}
                      className="text-gold hover:text-gold/80"
                    >
                      {copiedField === 'Sort Code' ? <Check size={16} /> : <Copy size={16} />}
                    </Button>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-black/30 rounded-lg border border-gold/20">
                    <div>
                      <p className="text-gold/70 text-xs uppercase">Account Number</p>
                      <p className="text-white font-semibold font-mono">{BANK_DETAILS.accountNumber}</p>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => copyToClipboard(BANK_DETAILS.accountNumber, 'Account Number')}
                      className="text-gold hover:text-gold/80"
                    >
                      {copiedField === 'Account Number' ? <Check size={16} /> : <Copy size={16} />}
                    </Button>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-gold/10 rounded-lg border border-gold/40">
                    <div>
                      <p className="text-gold/70 text-xs uppercase">Payment Reference (Important!)</p>
                      <p className="text-gold font-bold font-mono text-lg">{paymentReference}</p>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => copyToClipboard(paymentReference, 'Reference')}
                      className="text-gold hover:text-gold/80"
                    >
                      {copiedField === 'Reference' ? <Check size={16} /> : <Copy size={16} />}
                    </Button>
                  </div>
                </div>
                
                <div className="mt-4 p-3 bg-amber-900/20 rounded-lg border border-amber-500/30">
                  <p className="text-amber-200 text-sm">
                    <strong>Important:</strong> Please use the payment reference above when making your transfer. 
                    This helps us match your payment to your booking quickly.
                  </p>
                </div>
              </div>
              
              <div className="flex justify-between gap-4">
                <Button
                  onClick={() => setStep(1)}
                  variant="outline"
                  className="border-gold/30 text-gold hover:bg-gold/10"
                >
                  Back to Details
                </Button>
                <Button
                  onClick={() => setStep(3)}
                  className="bg-gold text-black hover:bg-gold/90 font-semibold px-8"
                >
                  Continue to Contract
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Contract */}
          {step === 3 && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h3 className="heading-display text-2xl text-gold">Review & Sign Contract</h3>
                <Button
                  onClick={downloadContractPdf}
                  disabled={downloadingPdf}
                  variant="outline"
                  className="border-gold/50 text-gold hover:bg-gold/10"
                  data-testid="download-contract-pdf"
                >
                  {downloadingPdf ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Download className="mr-2 h-4 w-4" />
                      Download PDF
                    </>
                  )}
                </Button>
              </div>
              
              <div ref={contractRef} className="bg-white p-6 rounded-xl border border-gold/30 max-h-[60vh] overflow-y-auto">
                {/* Contract Header */}
                <div className="text-center border-b border-gray-300 pb-6 mb-6">
                  <img src={LOGO_URL} alt="Elvis One More Time" className="h-20 mx-auto mb-4" />
                  <h2 className="text-2xl font-bold text-black">PERFORMANCE AGREEMENT</h2>
                  <div className="text-gray-600 text-sm mt-2">
                    <p>{BUSINESS_DETAILS.address}</p>
                    <p>Tel: {BUSINESS_DETAILS.phone} | Email: {BUSINESS_DETAILS.email}</p>
                  </div>
                </div>

                {/* Event Details */}
                <div className="mb-6">
                  <h3 className="text-lg font-bold text-black mb-3 border-b border-gray-200 pb-2">Event Details</h3>
                  <div className="grid md:grid-cols-2 gap-4 text-sm text-gray-800">
                    <div><strong>Event Date:</strong> {selectedDate?.toLocaleDateString('en-GB', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</div>
                    <div><strong>Event Type:</strong> {bookingData.eventType?.charAt(0).toUpperCase() + bookingData.eventType?.slice(1)}</div>
                    <div><strong>Venue:</strong> {bookingData.venueName}</div>
                    <div><strong>Show Format:</strong> {bookingData.showFormat === '1x60' ? '1 x 60 minutes' : '2 x 45 minutes'}</div>
                    <div className="md:col-span-2"><strong>Venue Address:</strong> {bookingData.venueAddress}</div>
                  </div>
                </div>

                {/* Client Details */}
                <div className="mb-6">
                  <h3 className="text-lg font-bold text-black mb-3 border-b border-gray-200 pb-2">Client Details</h3>
                  <div className="grid md:grid-cols-2 gap-4 text-sm text-gray-800">
                    <div><strong>Contact Name:</strong> {bookingData.contactName}</div>
                    <div><strong>Phone:</strong> {bookingData.phone}</div>
                    <div><strong>Email:</strong> {bookingData.email}</div>
                  </div>
                </div>

                {/* Schedule */}
                <div className="mb-6">
                  <h3 className="text-lg font-bold text-black mb-3 border-b border-gray-200 pb-2">Schedule</h3>
                  <div className="grid md:grid-cols-3 gap-4 text-sm text-gray-800">
                    <div><strong>Arrival Time:</strong> {bookingData.arrivalTime}</div>
                    <div><strong>Setup Time:</strong> {bookingData.setupTime}</div>
                    <div><strong>Performance Time:</strong> {bookingData.performanceTime}</div>
                  </div>
                </div>

                {/* Financial Terms */}
                <div className="mb-6">
                  <h3 className="text-lg font-bold text-black mb-3 border-b border-gray-200 pb-2">Financial Terms</h3>
                  <div className="grid md:grid-cols-3 gap-4 text-sm text-gray-800">
                    <div><strong>Performance Fee:</strong> £{parseFloat(bookingData.fee).toFixed(2)}</div>
                    <div><strong>Deposit (20%):</strong> £{calculateDeposit()}</div>
                    <div><strong>Balance Due:</strong> £{(parseFloat(bookingData.fee) * 0.8).toFixed(2)}</div>
                  </div>
                  <p className="text-sm text-gray-600 mt-2">Payment Reference: <strong>{paymentReference}</strong></p>
                </div>

                {/* Cancellation Policy */}
                <div className="mb-6">
                  <h3 className="text-lg font-bold text-black mb-3 border-b border-gray-200 pb-2">Cancellation Policy</h3>
                  <div className="text-sm text-gray-800 space-y-3">
                    <div>
                      <h4 className="font-semibold">Booking Deposit</h4>
                      <p>A non-refundable deposit equal to 20% of the total fee shall be payable at the time of booking in order to secure the performance date. The booking shall not be confirmed until the deposit has been received.</p>
                    </div>
                    
                    <div>
                      <h4 className="font-semibold">Cancellation by the Client</h4>
                      <p>All cancellations must be made in writing by email and shall only take effect from the date such written notice is received by the Artist.</p>
                    </div>
                    
                    <div>
                      <h4 className="font-semibold">Cancellation Charges</h4>
                      <p>In the event of cancellation by the Client, the following charges shall apply:</p>
                      <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
                        <li><strong>More than 90 days before:</strong> The Client shall forfeit the non-refundable deposit only.</li>
                        <li><strong>Between 30 and 90 days before:</strong> The Client shall be liable to pay 50% of the total fee, less any deposit already paid.</li>
                        <li><strong>Less than 30 days before:</strong> The Client shall be liable to pay 100% of the total fee, less any deposit already paid.</li>
                        <li><strong>High-demand/peak-season dates (e.g., New Year's Eve):</strong> The Artist reserves the right to require 100% of the total fee at an earlier stage, where expressly stated.</li>
                      </ul>
                    </div>
                    
                    <div>
                      <h4 className="font-semibold">Rescheduling</h4>
                      <p>Where possible, the Client may request that the booking be transferred to a new date instead of being cancelled. Any rescheduling shall be subject to the Artist's availability and shall only be effective once confirmed in writing by the Artist.</p>
                    </div>
                    
                    <div>
                      <h4 className="font-semibold">Force Majeure</h4>
                      <p>Neither party shall be liable for cancellation or failure to perform where such cancellation or non-performance arises from events beyond their reasonable control, including but not limited to serious illness, accident, bereavement, natural disaster, government restriction, industrial dispute, epidemic, pandemic, civil emergency, or transport disruption.</p>
                    </div>
                    
                    <div>
                      <h4 className="font-semibold">Cancellation by the Artist</h4>
                      <p>If the Artist cancels the booking, other than for reasons caused by the Client or under the force majeure clause above, the Artist shall refund the deposit and any other sums paid by the Client in respect of the booking.</p>
                    </div>
                    
                    <div>
                      <h4 className="font-semibold">Non-Performance and Non-Appearance</h4>
                      <p>If the Artist fails to attend or perform at the event, other than due to force majeure or the Client's breach of this Agreement, the Artist's liability shall be limited to repayment of sums paid by the Client, up to a maximum of the agreed total fee. The Artist shall not be liable for any indirect or consequential losses.</p>
                    </div>
                  </div>
                </div>

                {/* Additional Terms */}
                <div className="mb-6">
                  <h3 className="text-lg font-bold text-black mb-3 border-b border-gray-200 pb-2">Additional Terms</h3>
                  <ul className="text-sm text-gray-800 list-disc list-inside space-y-1">
                    <li>Full public liability insurance provided</li>
                    <li>PAT tested equipment certificates available on request</li>
                    <li>Balance payment due 2 days before the event</li>
                  </ul>
                </div>

                {bookingData.notes && (
                  <div className="mb-6">
                    <h3 className="text-lg font-bold text-black mb-3 border-b border-gray-200 pb-2">Special Requirements/Notes</h3>
                    <p className="text-sm text-gray-800">{bookingData.notes}</p>
                  </div>
                )}

                {/* Artist Signature */}
                <div className="mt-8 pt-6 border-t border-gray-300">
                  <div className="grid md:grid-cols-2 gap-8">
                    <div>
                      <p className="text-sm text-gray-600 mb-2">Artist Signature:</p>
                      <img src={ARTIST_SIGNATURE_URL} alt="Gary Jay Signature" className="h-16 object-contain" />
                      <p className="text-sm text-gray-800 mt-2"><strong>Gary Jay</strong></p>
                      <p className="text-xs text-gray-500">Elvis - One More Time</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-2">Client Signature:</p>
                      <div className="border-b-2 border-gray-400 h-16 flex items-end">
                        {signatureName && (
                          <p className="text-2xl font-script text-gray-800 italic">{signatureName}</p>
                        )}
                      </div>
                      <p className="text-sm text-gray-800 mt-2"><strong>{bookingData.contactName || 'Client Name'}</strong></p>
                      <p className="text-xs text-gray-500">{new Date().toLocaleDateString('en-GB')}</p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Signature Input */}
              <div className="bg-black/50 p-6 rounded-xl border border-gold/30">
                <p className="text-white/90 mb-4">
                  By typing your full name below, you confirm that you have read, understood, and agree to the terms and conditions outlined in this contract.
                </p>
                <Input
                  placeholder="Type your full name to sign"
                  value={signatureName}
                  onChange={(e) => setSignatureName(e.target.value)}
                  className="bg-black/50 border-gold/30 focus:border-gold text-white mb-4"
                  data-testid="contract-signature-input"
                />
                <label className="flex items-center gap-3 text-white/90 mb-4">
                  <input
                    type="checkbox"
                    checked={agreedToTerms}
                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                    className="w-5 h-5 accent-gold"
                    data-testid="agree-terms-checkbox"
                  />
                  I agree to the terms and conditions of this contract
                </label>
                <Button
                  onClick={handleContractSign}
                  disabled={loading || !signatureName.trim() || !agreedToTerms}
                  className="w-full bg-gold text-black hover:bg-gold/90 font-semibold py-6"
                  data-testid="sign-contract-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Signing Contract...
                    </>
                  ) : (
                    'Sign Contract & Complete Booking'
                  )}
                </Button>
              </div>
              
              <div className="flex justify-start">
                <Button
                  onClick={() => setStep(2)}
                  variant="outline"
                  className="border-gold/30 text-gold hover:bg-gold/10"
                >
                  Back to Payment Details
                </Button>
              </div>
            </div>
          )}

          {/* Step 4: Confirmation */}
          {step === 4 && (
            <div className="text-center space-y-6 py-8">
              <div className="flex justify-center">
                <CheckCircle className="text-gold" size={80} />
              </div>
              
              <h3 className="heading-display text-3xl text-gold">Booking Complete!</h3>
              
              <div className="bg-black/50 p-6 rounded-xl border border-gold/30 text-left max-w-2xl mx-auto">
                <h4 className="text-lg text-gold mb-4 font-semibold">What Happens Next?</h4>
                <ul className="space-y-3 text-white/90">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="text-gold flex-shrink-0 mt-1" size={20} />
                    <span>Confirmation email sent to {bookingData.email}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="text-gold flex-shrink-0 mt-1" size={20} />
                    <span>Signed contract and receipt will be emailed shortly</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="text-gold flex-shrink-0 mt-1" size={20} />
                    <span>Event confirmed for {selectedDate?.toLocaleDateString('en-GB', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="text-gold flex-shrink-0 mt-1" size={20} />
                    <span>Balance of £{(parseFloat(bookingData.fee) * 0.8).toFixed(2)} due 2 days before event</span>
                  </li>
                </ul>
              </div>
              
              <p className="text-silver">
                Booking Reference: <span className="text-gold font-mono">{bookingId}</span>
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  onClick={downloadContractPdf}
                  disabled={downloadingPdf}
                  variant="outline"
                  className="border-gold text-gold hover:bg-gold/10 font-semibold px-8 py-6"
                  data-testid="download-contract-confirmation"
                >
                  {downloadingPdf ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Generating PDF...
                    </>
                  ) : (
                    <>
                      <Download className="mr-2 h-5 w-5" />
                      Download Contract
                    </>
                  )}
                </Button>
                <Button
                  onClick={handleFinish}
                  className="bg-gold text-black hover:bg-gold/90 font-semibold px-12 py-6 text-lg"
                >
                  Done
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BookingModal;
